'use client';

import { useEffect, useRef } from 'react';

/**
 * Событие показа денежного блока — пара к affiliate_click.
 *
 * Зачем: без показов нельзя отличить «до карточки не доходят» от «доходят и не кликают».
 * На мобильном scroll-90 — 3.3%, а подвал «Рекомендуемые сервисы» стоит на 64–77% высоты
 * статьи; клик на показ — единственный способ решить, двигать блок или менять оффер
 * (исследование 30.09.2026, L19). Параметры те же, что у affiliate_click, — регистрировать
 * в GA4 ничего не нужно.
 *
 * Показ = ≥50% площади блока (или, у высоких блоков вроде подвала, ≥половины экрана) в окне
 * не меньше секунды, один раз на блок за просмотр.
 * gtag грузится лениво (lazyOnload, до ~4 с), поэтому ранние показы копятся в очереди.
 */
const VIEW_MS = 1000;
const queue = [];
let flushTimer = null;

function flush() {
    if (typeof window === 'undefined' || !window.gtag) return false;
    while (queue.length) window.gtag('event', 'offer_view', queue.shift());
    return true;
}

function send(params) {
    queue.push({ ...params, page_path: window.location.pathname });
    if (flush() || flushTimer) return;
    let tries = 0;
    flushTimer = setInterval(() => {
        tries += 1;
        if (flush() || tries > 20) {
            clearInterval(flushTimer);
            flushTimer = null;
        }
    }, 1000);
}

/**
 * @param {{provider?: string, vertical?: string, country?: string, position?: string}} params
 * @returns ref для корневого элемента блока
 */
export function useOfferView({ provider, vertical, country, position } = {}) {
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el || typeof IntersectionObserver === 'undefined') return undefined;
        let timer = null;
        let sent = false;
        const observer = new IntersectionObserver(([entry]) => {
            if (sent) return;
            const seen = entry.isIntersecting && (entry.intersectionRatio >= 0.5
                || entry.intersectionRect.height >= window.innerHeight * 0.5);
            if (seen) {
                if (timer) return;
                timer = setTimeout(() => {
                    sent = true;
                    observer.disconnect();
                    send({
                        provider: provider || undefined,
                        vertical: vertical || undefined,
                        country: country || undefined,
                        position: position || undefined,
                    });
                }, VIEW_MS);
            } else if (timer) {
                clearTimeout(timer);
                timer = null;
            }
        }, { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] });
        observer.observe(el);
        return () => {
            if (timer) clearTimeout(timer);
            observer.disconnect();
        };
    }, [provider, vertical, country, position]);

    return ref;
}

/**
 * Обёртка для компонентов с ранними return: хук нельзя звать после них, а
 * корневой элемент блока — можно отрисовать через неё.
 */
export function OfferView({ as: Tag = 'div', provider, vertical, country, position, children, ...rest }) {
    const ref = useOfferView({ provider, vertical, country, position });
    return <Tag ref={ref} {...rest}>{children}</Tag>;
}
